import React, { useState, useEffect } from "react";
import "./EmployeeTrainingCompletion.css";
import * as XLSX from "xlsx";
import { empTrainingCompletionAPI } from "./api";
import { getDistinctFilters } from "../Components/Menu/Branch";
import DataTable from "./DataTable";
import FuncAreaViewer from "../Components/Viewers/FuncAreaViewer";
import TrngCourseViewer from "../Components/Viewers/TrngCourseViewer";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";


const summaryColumns = [
  { header: "Sr. No.", accessor: (row, idx) => row.sr_no ?? idx + 1, className: "col-narrow" },
  { header: "Functional Area", accessor: (row) => row.func_sub_area_name ?? "" },
  { header: "Training Course", accessor: (row) => row.trng_course_name ?? "" },
  { header: "< 30 Days", accessor: (row) => row.lte_30_days ?? "", className: "col-number" },
  { header: "31–90 Days", accessor: (row) => row.days_31_90 ?? "", className: "col-number" },
  { header: "91–180 Days", accessor: (row) => row.days_91_180 ?? "", className: "col-number" },
  { header: "181–270 Days", accessor: (row) => row.days_181_270 ?? "", className: "col-number" },
  { header: "> 270 Days", accessor: (row) => row.gt_270_days ?? "", className: "col-number" },
];


const detailColumns = [
  { header: "Sr. No.", accessor: (row, idx) => row.sr_no ?? idx + 1, className: "col-narrow" },
  { header: "Functional Name", accessor: (row) => row.func_sub_area_name ?? "" },
  { header: "Zone", accessor: (row) => row.zone ?? "" },
  { header: "Branch", accessor: (row) => row.branch ?? "" },
  { header: "Franchisee Name", accessor: (row) => row.franchisee_name ?? "" },
  { header: "Training Course Name", accessor: (row) => row.trng_course_name ?? "" },
  { header: "Name", accessor: (row) => row.emp_name ?? "" },
  { header: "Sal Code", accessor: (row) => row.sal_code ?? "" },
  { header: "Date Of Identification", accessor: (row) => { if (!row.date_of_need) return ''; const d = row.date_of_need.split('T')[0]; const [y,m,dd] = d.split('-'); return `${dd}/${m}/${y}`; } },
  { header: "Training Attended", accessor: (row) => row.status ?? "" },
  { header: "Batch No.", accessor: (row) => row.nomin_batch_sr_no ?? "" },
  { header: "Training Start Date", accessor: (row) => { if (!row.batch_start_date) return ''; const d = row.batch_start_date.split('T')[0]; const [y,m,dd] = d.split('-'); return `${dd}/${m}/${y}`; } },
  { header: "Date of Nomination", accessor: (row) => { if (!row.date_of_first_nomination) return ''; const d = row.date_of_first_nomination.split('T')[0]; const [y,m,dd] = d.split('-'); return `${dd}/${m}/${y}`; } },
  { header: "Training Attended Hrs", accessor: (row) => row.training_attended_hrs ?? "" },
];

function EmployeeTrainingCompletion() {
  const [activeView, setActiveView] = useState(null);

  // filter states
  const [funcAreaName, setFuncAreaName] = useState("");
  const [funcSubAreaCode, setFuncSubAreaCode] = useState("");
  const [trngCourseName, setTrngCourseName] = useState("");
  const [trngCourseCode, setTrngCourseCode] = useState("");
  const [empCodeFrom, setEmpCodeFrom] = useState("");
  const [empCodeTo, setEmpCodeTo] = useState("");
  const [zoneName, setZoneName] = useState("");
  const [branchName, setBranchName] = useState("");
  const [franchiseeName, setFranchiseeName] = useState("");
  const [periodStart, setPeriodStart] = useState(null);
  const [periodEnd, setPeriodEnd] = useState(null);
  const [periodStartOpen, setPeriodStartOpen] = useState(false);
  const [periodEndOpen, setPeriodEndOpen] = useState(false);

  // table data
  const [summaryRows, setSummaryRows] = useState([]);
  const [detailRows, setDetailRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [branches, setBranches] = useState([]);
  const [zones, setZones] = useState([]);
  const [franchises, setFranchises] = useState([]);

  useEffect(() => {
    getDistinctFilters().then(({ branches: b, zones: z, franchises: f }) => {
      setBranches(b);
      setZones(z);
      setFranchises(f);
    });
  }, []);

  // viewer modals
  const [showFuncAreaViewer, setShowFuncAreaViewer] = useState(false);
  const [showCourseViewer, setShowCourseViewer] = useState(false);

  const handleFuncAreaSelect = (row) => {
    setFuncAreaName(row.func_sub_area_name ?? "");
    setFuncSubAreaCode(row.func_sub_area_code ?? "");
    setTrngCourseName("");
    setTrngCourseCode("");
    setShowFuncAreaViewer(false);
  };

  const handleCourseSelect = (row) => {
    setTrngCourseName(row.trng_course_name ?? "");
    setTrngCourseCode(row.trng_course_code ?? "");
    setShowCourseViewer(false);
  };

  const buildParams = () => {
    const p = {};
    if (funcSubAreaCode) p.func_sub_area_code = funcSubAreaCode;
    if (trngCourseCode) p.trng_course_code = trngCourseCode;
    if (empCodeFrom.trim()) p.emp_from_sal_code = empCodeFrom.trim();
    if (empCodeTo.trim()) p.emp_to_sal_code = empCodeTo.trim();
    if (zoneName.trim()) p.zone = zoneName.trim();
    if (branchName.trim()) p.branch = branchName.trim();
    if (franchiseeName.trim()) p.franchisee_name = franchiseeName.trim();
    if (periodStart) p.period_start = `${periodStart.getFullYear()}-${String(periodStart.getMonth()+1).padStart(2,'0')}-${String(periodStart.getDate()).padStart(2,'0')}`;
    if (periodEnd) p.period_end = `${periodEnd.getFullYear()}-${String(periodEnd.getMonth()+1).padStart(2,'0')}-${String(periodEnd.getDate()).padStart(2,'0')}`;
    return p;
  };

  const handleSummary = async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const data = await empTrainingCompletionAPI.getSummary(buildParams());
      setSummaryRows(Array.isArray(data) && data.length > 0 ? data : []);
      setActiveView("summary");
    } catch (err) {
      setErrorMsg("Failed to load summary. Please try again.");
      setSummaryRows([]);
      setActiveView("summary");
    } finally {
      setLoading(false);
    }
  };

  const handleDetails = async () => {
    setDetailLoading(true);
    setErrorMsg(null);
    try {
      const data = await empTrainingCompletionAPI.getDetails({ ...buildParams(), status: 'Attended' });
      console.log('[EmpTrainingCompletion] details response sample:', Array.isArray(data) ? data.slice(0, 3).map(r => ({ sal_code: r.sal_code, nomin_batch_sr_no: r.nomin_batch_sr_no, batch_start_date: r.batch_start_date, training_attended_hrs: r.training_attended_hrs })) : data);
      setDetailRows(Array.isArray(data) && data.length > 0 ? data : []);
      setActiveView("details");
    } catch (err) {
      setErrorMsg("Failed to load details. Please try again.");
      setDetailRows([]);
      setActiveView("details");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleReset = () => {
    setFuncAreaName("");
    setFuncSubAreaCode("");
    setTrngCourseName("");
    setTrngCourseCode("");
    setEmpCodeFrom("");
    setEmpCodeTo("");
    setZoneName("");
    setBranchName("");
    setFranchiseeName("");
    setPeriodStart(null);
    setPeriodEnd(null);
    setPeriodStartOpen(false);
    setPeriodEndOpen(false);
    setSummaryRows([]);
    setDetailRows([]);
    setActiveView(null);
    setErrorMsg(null);
  };

  const handleExport = (view) => {
    const today = new Date().toLocaleDateString("en-GB");
    const isSummary = view === "summary";
    const cols = isSummary ? summaryColumns : detailColumns;
    const rows = isSummary ? summaryRows : detailRows;
    const headers = cols.map((c) => c.header);
    const data = rows.map((row, idx) => cols.map((c) => c.accessor(row, idx)));
    const aoa = [
      ["Report Title : Employee wise Training Completion Details"],
      [`Report Date : ${today}`],
      [],
      headers,
      ...data,
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isSummary ? "Summary" : "Details");
    XLSX.writeFile(wb, `EmployeeTrainingCompletion_${view}.xlsx`);
  };

  return (
    <div className="etcmp-container">

      {/* FILTERS */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 4 }}>
        <table style={{ fontFamily: "GEG-Body, sans-serif", fontSize: 12.5 }}>
          <tbody>
            <tr>
              <td className="etcmp-label" style={{ paddingRight: 6 }}>Function Area Name</td>
              <td style={{ paddingRight: 40 }}>
                <input className="etcmp-input" style={{ width: 160 }} type="text" value={funcAreaName} readOnly onFocus={(e) => e.target.blur()} />
                <button className="etcmp-btn etcmp-btn-outline" type="button" onClick={() => setShowFuncAreaViewer(true)}>Get</button>
                <button className="etcmp-btn etcmp-btn-secondary" type="button" onClick={() => { setFuncAreaName(""); setFuncSubAreaCode(""); setTrngCourseName(""); setTrngCourseCode(""); }}>Clear</button>
              </td>
              <td className="etcmp-label" style={{ paddingRight: 6 }}>Training Course Name</td>
              <td>
                <input className="etcmp-input" style={{ width: 160 }} type="text" value={trngCourseName} readOnly onFocus={(e) => e.target.blur()} />
                <button className="etcmp-btn etcmp-btn-outline" type="button" onClick={() => {
                  if (!funcSubAreaCode) { alert('Please select a Function Area first.'); return; }
                  setShowCourseViewer(true);
                }}>Get</button>
                <button className="etcmp-btn etcmp-btn-secondary" type="button" onClick={() => { setTrngCourseName(""); setTrngCourseCode(""); }}>Clear</button>
              </td>
            </tr>
            <tr>
              <td className="etcmp-label" style={{ paddingRight: 6 }}>Employee Code From</td>
              <td style={{ paddingRight: 40 }}>
                <input className="etcmp-input" style={{ width: 160 }} type="text" value={empCodeFrom} onChange={(e) => setEmpCodeFrom(e.target.value)} />
              </td>
              <td className="etcmp-label" style={{ paddingRight: 6 }}>Employee Code To</td>
              <td>
                <input className="etcmp-input" style={{ width: 160 }} type="text" value={empCodeTo} onChange={(e) => setEmpCodeTo(e.target.value)} />
              </td>
            </tr>
            <tr>
              <td className="etcmp-label" style={{ paddingRight: 6 }}>Zone Name</td>
              <td style={{ paddingRight: 40 }}>
                <select className="etcmp-input" style={{ width: 160 }} value={zoneName} onChange={(e) => setZoneName(e.target.value)}>
                  <option value="">-- Select --</option>
                  {zones.map((z) => (<option key={z} value={z}>{z}</option>))}
                </select>
              </td>
              <td className="etcmp-label" style={{ paddingRight: 6 }}>Branch Name</td>
              <td>
                <select className="etcmp-input" style={{ width: 160 }} value={branchName} onChange={(e) => setBranchName(e.target.value)}>
                  <option value="">-- Select --</option>
                  {branches.map((b) => (<option key={b} value={b}>{b}</option>))}
                </select>
              </td>
            </tr>
            <tr>
              <td className="etcmp-label" style={{ paddingRight: 6 }}>Franchisee Name</td>
              <td style={{ paddingRight: 40 }}>
                <select className="etcmp-input" style={{ width: 160 }} value={franchiseeName} onChange={(e) => setFranchiseeName(e.target.value)}>
                  <option value="">-- Select --</option>
                  {franchises.map((f) => (<option key={f} value={f}>{f}</option>))}
                </select>
              </td>
              <td className="etcmp-label" style={{ paddingRight: 6 }}>Period Start</td>
              <td>
                <div className="ann-date-wrap" onClick={() => setPeriodStartOpen(true)}>
                  <DatePicker
                    open={periodStartOpen}
                    onClickOutside={() => setPeriodStartOpen(false)}
                    selected={periodStart}
                    onChange={(date) => { setPeriodStart(date); setPeriodStartOpen(false); }}
                    dateFormat="dd/MM/yyyy"
                    className="ann-date-input"
                    placeholderText="Select date"
                    showIcon
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    customInput={<input readOnly />}
                  />
                </div>
              </td>
            </tr>
            <tr>
              <td className="etcmp-label" style={{ paddingRight: 6 }}>Period End</td>
              <td style={{ paddingRight: 40 }}>
                <div className="ann-date-wrap" onClick={() => setPeriodEndOpen(true)}>
                  <DatePicker
                    open={periodEndOpen}
                    onClickOutside={() => setPeriodEndOpen(false)}
                    selected={periodEnd}
                    onChange={(date) => { setPeriodEnd(date); setPeriodEndOpen(false); }}
                    dateFormat="dd/MM/yyyy"
                    className="ann-date-input"
                    placeholderText="Select date"
                    minDate={periodStart}
                    showIcon
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    customInput={<input readOnly />}
                  />
                </div>
              </td>
              <td></td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ACTION ROW */}
      <div className="etcmp-action-row">
        <button className="etcmp-btn etcmp-btn-outline" onClick={handleReset}>Reset</button>
        <select className="etcmp-input-small">
          <option value="">--- Sort On: ---</option>
          <option>Functional Area</option>
          <option>Training Course</option>
          <option>Zone</option>
          <option>Branch</option>
        </select>
        <select className="etcmp-input-small">
          <option>ExactMatch</option>
          <option>Contains</option>
        </select>
        <select className="etcmp-input-small">
          <option>Any</option>
          <option>All</option>
        </select>
        <button className="etcmp-btn etcmp-btn-outline" onClick={handleSummary} disabled={loading}>
          {loading ? "Loading…" : "Summary"}
        </button>
        <button className="etcmp-btn etcmp-btn-outline" onClick={handleDetails} disabled={detailLoading}>
          {detailLoading ? "Loading…" : "Details"}
        </button>
      </div>

      {errorMsg && <p className="etcmp-error">{errorMsg}</p>}

      {/* TABLE AREA */}
      {activeView !== null && (
        <div className="etcmp-details">
          <p className="etcmp-section-title">
            {activeView === "summary" ? "Employee Training Completion — Summary" : "Employee Training Completion — Details"}
          </p>
          <DataTable
            columns={activeView === "summary" ? summaryColumns : detailColumns}
            data={activeView === "summary" ? summaryRows : detailRows}
            showEditCols={false}
            editRows={{}}
            onToggleEditRow={() => {}}
            onChangeEditCell={() => {}}
            selectedForDelete={{}}
            onToggleDeleteRow={() => {}}
            emptyMessage="No data to display"
            rowKey={(_row, idx) => idx}
          />
          {(activeView === "summary" ? summaryRows : detailRows).length > 0 && (
            <div style={{ display: "flex", justifyContent: "center", margin: "12px 0" }}>
              <button className="etcmp-btn etcmp-btn-outline" onClick={() => handleExport(activeView)}>Export to Excel</button>
            </div>
          )}
        </div>
      )}

      {/* FuncArea Viewer Modal */}
      {showFuncAreaViewer && (
        <div className="etcmp-overlay" onClick={() => setShowFuncAreaViewer(false)}>
          <div className="etcmp-modal" onClick={(e) => e.stopPropagation()}>
            <button className="etcmp-modal-close" onClick={() => setShowFuncAreaViewer(false)}>&times;</button>
            <FuncAreaViewer selectable onSelect={handleFuncAreaSelect} autoLoad />
          </div>
        </div>
      )}

      {/* TrngCourse Viewer Modal */}
      {showCourseViewer && (
        <div className="etcmp-overlay" onClick={() => setShowCourseViewer(false)}>
          <div className="etcmp-modal" onClick={(e) => e.stopPropagation()}>
            <button className="etcmp-modal-close" onClick={() => setShowCourseViewer(false)}>&times;</button>
            <TrngCourseViewer selectable onSelect={handleCourseSelect} funcSubAreaCode={funcSubAreaCode} autoLoad />
          </div>
        </div>
      )}

    </div>
  );
}

export default EmployeeTrainingCompletion;